import type { ReactElement } from 'react';

import type { Control, FieldValues, Path } from 'react-hook-form';
import { Controller } from 'react-hook-form';

import { normalizeTextInput } from '~/utils/normalizeTextInput';

type FormFieldRenderProps<TValue> = {
    error?: string;
    onBlur: () => void;
    onChange: (value: TValue) => void;
    value: TValue;
};

type FormFieldProps<TFieldValues extends FieldValues, TValue> = {
    children: (field: FormFieldRenderProps<TValue>) => ReactElement;
    control: Control<TFieldValues>;
    name: Path<TFieldValues>;
};

/**
 * Bridges react-hook-form to the controlled `cpool-components` inputs
 * (CurrencyInput, NumberInput, PercentInput, DateInput, ...) that expose a
 * custom `value`/`onChange(value)` contract instead of native DOM events.
 *
 * It centralises the `Controller` boilerplate so each `FormXxxInput` only has to
 * map the field state onto its underlying component.
 *
 * @example
 * <FormField<MyForm, number | null> control={control} name="amount">
 *     {({ error, onChange, value }) => (
 *         <CurrencyInput id="amount" value={value} onChange={onChange} error={error} />
 *     )}
 * </FormField>
 */
export const FormField = <TFieldValues extends FieldValues, TValue>({
    children,
    control,
    name,
}: FormFieldProps<TFieldValues, TValue>) => (
    <Controller
        control={control}
        name={name}
        render={({ field, fieldState: { error } }) =>
            children({
                error: error?.message,
                onBlur: () => {
                    if (typeof field.value !== 'string') return;
                    const normalized = normalizeTextInput(field.value);
                    if (normalized !== field.value) field.onChange(normalized);
                },
                onChange: (value: TValue) => field.onChange(value),
                value: field.value as TValue,
            })
        }
    />
);
